<template>
  <section class="calculator-result">
    <div class="grid">
      <grid-item
        v-for="(item, index) in griditems"
        :key="index"
        :name="item.name"
        :color="item.color"
      >
        <template #value>
          {{ item.prepend
          }}<animated-number
            :formatValue="item.filter"
            :round="item.round"
            :value="item.value"
            :duration="500"
          />{{ item.append }}
        </template>
        <template #label>{{ item.label }}</template>
        <template #description>{{ item.description }}</template>
      </grid-item>

      <div class="row-opbrengst">
        <h3 class="opbrengst-wrapper">
          <span class="opbrengst-value">
            <animated-number
              :formatValue="formatEuro"
              :value="result.totaalOpbrengst"
              :duration="500"
            />
            *
          </span>
          <span class="opbrengst-label">Opbrengst per jaar</span>
        </h3>
      </div>

      <div class="trechter">
        <div class="trechter-segment"></div>
        <div class="trechter-segment"></div>
        <div class="trechter-segment"></div>
        <div class="trechter-segment"></div>
        <div class="trechter-segment"></div>
        <div class="trechter-image"></div>
      </div>
      <div class="trechter-top"></div>
    </div>
    <div class="footer">
      <div class="buttons">
        <calculator-button
          :isLink="true"
          :url="'https://moving.digital/pakket_samenstellen/'"
        >
          Stel je pakket samen
        </calculator-button>
        <calculator-button
          :isLink="true"
          :url="'tel:+31152023032'"
          :clear="true"
        >
          Hulp nodig? Neem direct contact op →
        </calculator-button>
      </div>
      <p class="disclaimer">
        * Opbrengst per jaar is berekend met gemiddelde kosten voor adviezen.
        Gebaseerd op gemiddeld € 2.500,- per aankoopadvies, gemiddeld € 2.750,-
        per verkoopadvies en gemiddeld € 2.250,- per hypotheekadvies.
      </p>
    </div>
  </section>
</template>

<script>
import { mapState } from "vuex";
import { formatEuro, formatThousands } from "../filters";

import AnimatedNumber from "animated-number-vue";
import GridItem from "./CalculatorGridItem";
import CalculatorButton from "./CalculatorButton.vue";

export default {
  components: {
    GridItem,
    AnimatedNumber,
    CalculatorButton
  },
  computed: {
    ...mapState(["result"]),
    griditems() {
      return [
        {
          name: "views",
          color: "var(--clr-oceanBlue)",
          value: this.result.views,
          round: 1,
          filter: formatThousands,
          label: "views",
          description:
            "op basis van 1 'te koop'-campagne en 1 'verkocht'-campagne per object"
        },
        {
          name: "bezichtigingen",
          color: "var(--clr-oceanBlue)",
          value: this.result.bezichtigingen,
          round: 1,
          filter: formatThousands,
          label: "bezichtigingen",
          description: "Gemiddeld 10 bezichtigingen per transactie"
        },
        {
          name: "clicks",
          color: "var(--clr-sortaRed)",
          value: this.result.clicks,
          round: 1,
          filter: formatThousands,
          label: "clicks",
          description: "Totaal aantal clicks naar eigen website"
        },
        {
          name: "aankoopadvies",
          color: "var(--clr-sortaRed)",
          value: this.result.aankoopadvies,
          round: 1,
          filter: formatThousands,
          append: "x",
          label: "aankoopadvies",
          description:
            "Aangevraagd door klanten tijdens de slimme survey (Surview)"
        },
        {
          name: "leads",
          color: "var(--clr-seemsLikeOrange)",
          value: this.result.leads,
          round: 1,
          filter: formatThousands,
          label: "potentiële leads",
          description: "Aantal potentiële leads"
        },
        {
          name: "verkoopadvies",
          color: "var(--clr-seemsLikeOrange)",
          value: this.result.verkoopadvies,
          round: 1,
          filter: formatThousands,
          append: "x",
          label: "verkoopadvies",
          description:
            "Aangevraagd door klanten tijdens de slimme survey (Surview)"
        },
        {
          name: "markt",
          color: "var(--clr-happyYellow)",
          value: this.result.markt,
          round: 1,
          filter: formatThousands,
          label: "op de markt",
          description: "Van alle leads is 25% op zoek naar een makelaar"
        },
        {
          name: "hypotheekadvies",
          color: "var(--clr-happyYellow)",
          value: this.result.hypotheekadvies,
          round: 1,
          filter: formatThousands,
          append: "x",
          label: "hypotheekadvies",
          description:
            "Aangevraagd door klanten tijdens de slimme survey (Surview)"
        },
        {
          name: "eigenAanbod",
          color: "var(--clr-grassGreen)",
          value: this.result.eigenAanbod,
          round: 1,
          filter: formatThousands,
          label: "in eigen aanbod",
          description: "Aantal potentiële leads"
        },
        {
          name: "budget",
          color: "var(--clr-blueGrayish)",
          value: this.result.budget,
          filter: formatEuro,
          prepend: "- ",
          label: "budget",
          description: "Aan advertentie en productkosten op jaarbasis"
        }
      ];
    }
  },
  methods: {
    formatEuro
  },
  mounted() {
    // open animation
    const element = this.$el;
    this.$anime({
      targets: element,
      height: [0, element.scrollHeight],
      duration: 500,
      easing: "easeInCubic",
      complete() {
        element.style.removeProperty("height");
        window.scroll({
          top: element.getBoundingClientRect().top - 100,
          behavior: "smooth"
        });
      }
    });
  }
};
</script>

<style lang="scss" scoped>
@media only screen and (min-width: 766px) {
  .calculator-result {
    --horizontal-overflow: 0 !important;
  }
}

.calculator-result {
  --horizontal-overflow: 7.3vw;
  overflow: hidden;
  margin-left: calc(var(--horizontal-overflow) * -1) !important;
  margin-right: calc(var(--horizontal-overflow) * -1) !important;
}

.grid {
  display: grid;
  grid-template-areas:
    "views        bezichtigingen  "
    "clicks       aankoopadvies   "
    "leads        verkoopadvies   "
    "markt        hypotheekadvies "
    "eigenAanbod  budget          "
    "opbrengst    opbrengst       ";
  grid-auto-columns: 1fr;
}

.row-opbrengst {
  background: var(--clr-green);
  text-align: center;
  grid-area: opbrengst;

  .opbrengst-wrapper {
    color: var(--clr-white);
    padding: var(--spacing-md) 0;
    display: block;
  }

  .opbrengst-value {
    display: block;
    font-size: var(--fs-44);
    font-weight: var(--fw-bold);
    line-height: 1;
  }

  .opbrengst-label {
    font-size: var(--fs-18);
    font-weight: var(--fw-regular);
  }
}

.footer {
  margin-left: var(--horizontal-overflow);
  margin-right: var(--horizontal-overflow);

  .buttons {
    margin: var(--spacing-lg) 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md) 0;

    .btn {
      flex-shrink: 0;
    }
  }

  .disclaimer {
    display: block;
    margin: var(--spacing-lg) 0;

    font-size: var(--fs-14);
    font-style: italic;
    color: var(--clr-fade);
  }
}

.trechter,
.trechter-top {
  display: none;
}

@media screen and (min-width: 989px) {
  .calculator-result {
    margin-top: calc(var(--spacing-lg) * -1);
  }

  .grid {
    grid-template-areas:
      "trechterTop  trechterTop  trechterTop      "
      "views        trechter     bezichtigingen   "
      "clicks       trechter     aankoopadvies    "
      "leads        trechter     verkoopadvies    "
      "markt        trechter     hypotheekadvies  "
      "eigenAanbod  trechter     budget           "
      "opbrengst    opbrengst    opbrengst        ";

    grid-template-columns: 3fr 4fr 3fr;
    grid-auto-columns: unset;
    grid-auto-rows: 1fr;
  }

  .row-opbrengst .opbrengst-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    height: 100%;
  }

  .footer .buttons {
    flex-direction: row;
  }

  .trechter {
    grid-area: trechter;
    display: flex;
    flex-direction: column;
    position: relative;

    .trechter-segment {
      height: 100%;
      border-top: 1px solid var(--clr-light);
    }

    .trechter-image {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url("https://moving.digital/wp-content/uploads/2020/12/trechter-bottom.svg")
        100% no-repeat;
    }
  }

  .trechter-top {
    grid-area: trechterTop;
    display: block;

    background: url("https://moving.digital/wp-content/uploads/2020/12/trechter-top.svg")
      center / 59.8% 100% no-repeat;
  }
}
</style>